import { Component, OnInit, Inject } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';

import { Message } from '../../../../../models/message';
import { ChatPoll, ChatPollOption, ChatPollVoter } from '../../../../../models/chat-poll';

import { ChatPollService } from '../chat-poll.service';
import { UserContactService } from '../../../../services/data/user-contact/user-contact.service';
import { AccountManagerService } from '../../../../services/account/account-manager.service';
import { TnNotificationService } from '../../../../../utilities/tn-notification/tn-notification.service';
import { TimestampService } from '../../../../../utilities/timestamp/timestamp.service';

/**
 * Poll detail / results dialog.
 *
 * Always fetches a fresh /chat_poll/result on open - voters are only ever
 * present in that response (never on the message envelope's `poll` object
 * / on a live 140), so a fetch is required every time this opens.
 */
@Component({
  selector: 'tn-poll-detail',
  templateUrl: './poll-detail.component.html',
  styleUrls: ['./poll-detail.component.scss']
})
export class PollDetailComponent implements OnInit {

  message: Message;
  isLoading: boolean = true;
  isReadOnly: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<PollDetailComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _chatPollService: ChatPollService,
    private _userContactService: UserContactService,
    private _accountManagerService: AccountManagerService,
    private _tnNotificationService: TnNotificationService,
    private _timestampService: TimestampService
  ) { }

  ngOnInit() {
    this.dialogRef.updateSize('50vw');

    this.message = this.data.message;
    this.isReadOnly = !!this.data.isReadOnly;
    this.refreshPollResult();
  }

  get poll(): ChatPoll {
    return this.message ? this.message.poll : null;
  }

  get isCreatedByMe(): boolean {
    return this.poll && this.poll.created_by === this._accountManagerService.userId;
  }

  get totalVoters(): number {
    return this.poll && this.poll.total_voters ? this.poll.total_voters : 0;
  }

  get pollId(): string {
    if (this.poll) {
      return this.poll.poll_id;
    }
    let body = this.message ? this.message.parsedBody : null;
    return body ? body.poll_id : null;
  }

  refreshPollResult(): void {
    if (!this.pollId && !(this.message && this.message.message_id)) {
      return;
    }
    this.isLoading = true;
    this._chatPollService.getPollResult(
      this.pollId,
      this.message.message_id,
      (resp: ChatPoll) => {
        this._chatPollService.mergePollResult(this.message.message_id, resp);
        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
        this._tnNotificationService.showSystemError();
      }
    );
  }

  getOptionPercentage(option: ChatPollOption): number {
    if (!this.totalVoters || !option.vote_count) {
      return 0;
    }
    return Math.round((option.vote_count / this.totalVoters) * 100);
  }

  getVoterDisplayName(voter: ChatPollVoter): string {
    let contact = this._userContactService.getUserContactByUserId(voter.user_id);
    return contact ? contact.name : voter.user_id;
  }

  checkIfVoterIsMe(voter: ChatPollVoter): boolean {
    return voter.user_id === this._accountManagerService.userId;
  }

  getVoterAvatar(voter: ChatPollVoter): string {
    let contact = this._userContactService.getUserContactByUserId(voter.user_id);
    return contact ? contact.avatarImageSrc : null;
  }

  /**
   * The vote day is shown relative for today / yesterday and as a plain date before that,
   * with the time alongside it - matching the mobile app's "yesterday 06:51 PM".
   */
  checkIfVotedToday(voter: ChatPollVoter): boolean {
    return this.getVoterDayOffset(voter) === 0;
  }

  checkIfVotedYesterday(voter: ChatPollVoter): boolean {
    return this.getVoterDayOffset(voter) === 1;
  }

  getVoterDate(voter: ChatPollVoter): string {
    return this._timestampService.getDateDisplay(voter.submit_date);
  }

  getVoterTime(voter: ChatPollVoter): string {
    return this._timestampService.getTime12HourDisplay(voter.submit_date);
  }

  /**
   * Marks the option(s) currently in the lead, so the outcome is readable without
   * comparing every row. Nothing is marked while no one has voted.
   */
  checkIfLeadingOption(option: ChatPollOption): boolean {
    if (this.poll.should_display_result !== 1) {
      return false;
    }
    let highest = 0;
    for (let o of this.poll.options) {
      highest = Math.max(highest, o.vote_count || 0);
    }
    return highest > 0 && (option.vote_count || 0) === highest;
  }

  private getVoterDayOffset(voter: ChatPollVoter): number {
    let voteDay = this._timestampService.getStartOfDateSecondStringByDateObj(
      this._timestampService.secondToDateObj(voter.submit_date)
    );
    let today = this._timestampService.getStartOfDateSecondStringByDateObj(new Date());
    return Math.round((parseFloat(today) - parseFloat(voteDay)) / 86400);
  }

  onEndPoll(): void {
    this._tnNotificationService.showPrompt(
      null,
      'WEBCLIENT.CHATROOM.POLL.END_CONFIRM',
      () => { },
      () => {
        this._chatPollService.endPoll(
          this.pollId,
          this.message.message_id,
          (resp) => {
            if (resp && resp.success === false) {
              this._tnNotificationService.showCustomWarningByTranslateKey('WEBCLIENT.CHATROOM.POLL.ALREADY_ENDED');
            }
            this.refreshPollResult();
          },
          () => {
            this._tnNotificationService.showSystemError();
          }
        );
      },
      false,
      true
    );
  }

  close(): void {
    this.dialogRef.close();
  }

}
